<script lang="ts" setup>
import SkrButton from '@/components/button/SkrButton.vue'
import SkrDivider from '@/components/SkrDivider.vue'
import BaseDialog from '@/components/dialogs/BaseDialog.vue'

const emit = defineEmits<{
  close: []
  action: [data: Dialogs['document/DialogAddGroup']['contextData']]
}>()

const { groupsRepository } = useApi()
const { t } = useI18n()
const isLoading = ref(false)
const showDialog = ref(true)
const groups = ref<Group[]>([])
const showAll = ref<Set<string>>(new Set())

const fetchGroups = async () => {
  isLoading.value = true
  groups.value = await groupsRepository.getGroups({ includeMembers: 'true', includeEmpty: 'false' })
  isLoading.value = false
}

const toggleShowAll = (groupId: string) => {
  if (showAll.value.has(groupId)) {
    showAll.value.delete(groupId)
  } else {
    showAll.value.add(groupId)
  }
}

const formatMembers = (group: Group) => {
  return group.members
    ?.slice(0, 3)
    .map(member => member.email)
    .join(', ')
}

const formatAllMembers = (group: Group) => {
  return group.members?.map(member => member.email).join(', ')
}

const hiddenMembersCount = (group: Group) => {
  return group.members && group.members.length > 3 ? group.members.length - 3 : 0
}

const onSelectGroup = (group: Group) => {
  isLoading.value = true
  emit('action', group)
}

onMounted(() => {
  fetchGroups()
})
</script>

<template>
  <base-dialog
    v-model="showDialog"
    max-width="640"
    class="dialog"
    :title="t('invite.add_group_dialog.title')"
    @click:outside="emit('close')"
  >
    <div v-if="isLoading" class="d-flex justify-center align-center">
      <skr-spinner size="small" />
    </div>
    <div v-else-if="groups.length === 0" class="mb-6">
      <p>
        {{ t('invite.add_group_dialog.no_signer_groups.title') }}
      </p>
      <p>
        {{ t('invite.add_group_dialog.no_signer_groups.subtitle.part_1') }}
        <strong>{{ t('invite.add_group_dialog.no_signer_groups.subtitle.part_2') }}</strong>
        {{ t('invite.add_group_dialog.no_signer_groups.subtitle.part_3') }}
      </p>
      <div class="mt-8 py-10 dialog__empty align-center d-flex justify-center">
        <v-icon color="grey-light" size="56">custom:users_group</v-icon>
      </div>
    </div>
    <div v-else class="dialog__content">
      <div class="mt-3">
        <div>
          <div v-for="group in groups" :key="group.id" class="group mb-4 py-3 px-6" @click="onSelectGroup(group)">
            <div class="group__title" data-cy="group_item">{{ group.name }}</div>
            <div v-if="showAll.has(group.id)">
              <div class="signer-group__subtitle">{{ formatAllMembers(group) }}</div>
            </div>
            <div v-else class="group__subtitle">
              <div class="">
                {{ formatMembers(group) }}
                <span
                  v-if="hiddenMembersCount(group)"
                  class="group__subtitle--toggle"
                  @click.stop="toggleShowAll(group.id)"
                >
                  +{{ hiddenMembersCount(group) }} {{ t('global.more') }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <template #actions>
      <div class="dialog__actions">
        <skr-divider />
        <v-card-actions class="d-flex justify-end">
          <skr-button class="dialog__cancel" size="lg" color="info" type="secondary" @click="emit('close')">
            {{ t('global.cancel') }}
          </skr-button>
        </v-card-actions>
      </div>
    </template>
  </base-dialog>
</template>

<style lang="scss" scoped>
.group {
  background-color: $c-grey-light-3;
  border-radius: 2 * $bw;
  &:hover {
    background-color: $c-blue-bg-3;
    cursor: pointer;
    .group__title {
      color: $c-primary;
    }
  }
  &__title {
    font-size: 1rem;
    font-weight: bold;
    color: $c-skribbleu;
    @include truncate-text;
  }
  &__subtitle {
    font-size: 0.75rem;
    color: $c-grey-dark;
    &--toggle {
      color: $c-primary;
      cursor: pointer;
      text-decoration: underline;
      &:hover {
        color: $c-skribbleu;
      }
    }
  }
}

.dialog {
  &__content {
    margin-top: 0.5 * $bw;
    margin-bottom: 2 * $bw;
    max-height: 400px;
    overflow-y: auto;
  }
  &__empty {
    background-color: $c-grey-light-3;
  }
  &__cancel {
    height: 48px;
  }
}
</style>
